// src/components/Navbar.jsx
import React, { useEffect, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import styled, { css } from "styled-components";
import { FiShoppingBag, FiMenu, FiX } from "react-icons/fi";
import { useCart } from "../context/CartContext";

const LINKS = [
  { to: "/", label: "Ballina", end: true },
  { to: "/produktet", label: "Produktet" },
  { to: "/rreth-nesh", label: "Rreth Nesh" },
  { to: "/kontakt", label: "Kontakt" },
];

export default function Navbar() {
  const { count, openCart } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    document.body.classList.toggle("no-scroll", menuOpen);
    return () => document.body.classList.remove("no-scroll");
  }, [menuOpen]);

  return (
    <>
      <Bar $scrolled={scrolled}>
        <Inner>
          <Brand to="/" onClick={() => setMenuOpen(false)}>
            <BrandName>Kaida</BrandName>
            <BrandSub>Beauty Studio</BrandSub>
          </Brand>

          <Links>
            {LINKS.map((l) => (
              <li key={l.to}>
                <StyledLink to={l.to} end={l.end}>
                  {l.label}
                </StyledLink>
              </li>
            ))}
          </Links>

          <Actions>
            <CartButton onClick={openCart} aria-label="Shporta">
              <FiShoppingBag />
              {count > 0 && <Badge>{count}</Badge>}
            </CartButton>

            <Burger
              onClick={() => setMenuOpen((v) => !v)}
              aria-label={menuOpen ? "Mbyll menunë" : "Hap menunë"}
              aria-expanded={menuOpen}
            >
              {menuOpen ? <FiX /> : <FiMenu />}
            </Burger>
          </Actions>
        </Inner>
      </Bar>

      <MobileMenu $open={menuOpen}>
        <ul>
          {LINKS.map((l) => (
            <li key={l.to}>
              <MobileLink
                to={l.to}
                end={l.end}
                onClick={() => setMenuOpen(false)}
              >
                {l.label}
              </MobileLink>
            </li>
          ))}
        </ul>
        <MenuFoot>
          Distributor ekskluziv i LaBina<sup>®</sup> për Kosovë
        </MenuFoot>
      </MobileMenu>
    </>
  );
}

/* ------------------------------------------------------------------ */

const Bar = styled.header`
  position: fixed;
  inset: 0 0 auto 0;
  z-index: 100;
  height: ${({ theme }) => theme.layout.navHeight};
  display: flex;
  align-items: center;
  transition:
    background 0.35s ${({ theme }) => theme.ease.out},
    box-shadow 0.35s ${({ theme }) => theme.ease.out};

  ${({ $scrolled, theme }) =>
    $scrolled
      ? css`
          background: rgba(255, 255, 255, 0.86);
          backdrop-filter: saturate(160%) blur(14px);
          box-shadow: 0 1px 0 ${theme.color.line};
        `
      : css`
          background: transparent;
        `}
`;

const Inner = styled.div`
  width: 100%;
  max-width: ${({ theme }) => theme.layout.maxWidth};
  margin: 0 auto;
  padding: 0 ${({ theme }) => theme.layout.gutter};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
`;

const Brand = styled(Link)`
  display: flex;
  flex-direction: column;
  line-height: 1;
`;

const BrandName = styled.span`
  font-family: ${({ theme }) => theme.font.display};
  font-size: 1.45rem;
  font-weight: ${({ theme }) => theme.weight.semi};
  color: ${({ theme }) => theme.color.ink};
  letter-spacing: -0.02em;
`;

const BrandSub = styled.span`
  font-size: 0.62rem;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.color.brand};
  margin-top: 4px;
`;

const Links = styled.ul`
  display: flex;
  align-items: center;
  gap: 34px;

  ${({ theme }) => theme.down.lg} {
    display: none;
  }
`;

const StyledLink = styled(NavLink)`
  position: relative;
  font-size: ${({ theme }) => theme.size.sm};
  font-weight: ${({ theme }) => theme.weight.medium};
  color: ${({ theme }) => theme.color.inkSoft};
  padding: 6px 0;
  transition: color 0.25s;

  &::after {
    content: "";
    position: absolute;
    left: 0;
    bottom: 0;
    height: 1.5px;
    width: 100%;
    background: ${({ theme }) => theme.color.brand};
    transform: scaleX(0);
    transform-origin: right;
    transition: transform 0.35s ${({ theme }) => theme.ease.out};
  }

  &:hover,
  &.active {
    color: ${({ theme }) => theme.color.brand};
  }
  &:hover::after,
  &.active::after {
    transform: scaleX(1);
    transform-origin: left;
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const IconButton = styled.button`
  width: 42px;
  height: 42px;
  display: grid;
  place-items: center;
  border-radius: ${({ theme }) => theme.radius.pill};
  color: ${({ theme }) => theme.color.ink};
  font-size: 1.15rem;
  transition: background 0.25s;

  &:hover {
    background: ${({ theme }) => theme.color.bgSoft};
  }
`;

const CartButton = styled(IconButton)`
  position: relative;
`;

const Badge = styled.span`
  position: absolute;
  top: 4px;
  right: 2px;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  display: grid;
  place-items: center;
  border-radius: ${({ theme }) => theme.radius.pill};
  background: ${({ theme }) => theme.color.brand};
  color: #fff;
  font-size: 0.66rem;
  font-weight: ${({ theme }) => theme.weight.bold};
  line-height: 1;
`;

const Burger = styled(IconButton)`
  ${({ theme }) => theme.up.lg} {
    display: none;
  }
`;

const MobileMenu = styled.nav`
  position: fixed;
  inset: ${({ theme }) => theme.layout.navHeight} 0 0 0;
  z-index: 99;
  background: ${({ theme }) => theme.color.bg};
  padding: 40px ${({ theme }) => theme.layout.gutter};
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  transition:
    opacity 0.3s ${({ theme }) => theme.ease.out},
    transform 0.35s ${({ theme }) => theme.ease.out},
    visibility 0.35s;

  ${({ $open }) =>
    $open
      ? css`
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        `
      : css`
          opacity: 0;
          visibility: hidden;
          transform: translateY(-12px);
        `}

  ${({ theme }) => theme.up.lg} {
    display: none;
  }
`;

const MobileLink = styled(NavLink)`
  display: block;
  padding: 16px 0;
  font-family: ${({ theme }) => theme.font.display};
  font-size: 1.9rem;
  color: ${({ theme }) => theme.color.ink};
  border-bottom: 1px solid ${({ theme }) => theme.color.lineSoft};

  &.active {
    color: ${({ theme }) => theme.color.brand};
  }
`;

const MenuFoot = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.size.xs};
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.color.muted};
`;
